import { ActionIcon, Button } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconDownload, IconShare } from '@tabler/icons-react';

import { useChartContext } from '@/contexts/ChartContext';

function canShareFiles(): boolean {
  if (typeof navigator === 'undefined' || !('share' in navigator) || !('canShare' in navigator)) {
    return false;
  }
  // Only use share on devices that can't hover (mobile/tablet)
  // Desktop browsers support Web Share API too, but download is more natural there
  if (typeof window !== 'undefined' && window.matchMedia('(hover: hover)').matches) {
    return false;
  }
  return true;
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

async function shareBlob(blob: Blob, filename: string) {
  const file = new File([blob], filename, { type: 'image/png' });
  const shareData = { files: [file] };

  if (navigator.canShare?.(shareData)) {
    await navigator.share(shareData);
  } else {
    // Fallback to download if sharing files isn't supported
    downloadBlob(blob, filename);
  }
}

export function DownloadButton() {
  const { exportFn } = useChartContext();
  const supportsShare = canShareFiles();

  const handleClick = async () => {
    const fn = exportFn.get();
    if (!fn) {
      notifications.show({
        title: 'Download not ready',
        message: 'Chart is still loading. Please wait a moment.',
        color: 'yellow',
      });
      return;
    }

    fn(async (blob, filename) => {
      if (supportsShare) {
        try {
          await shareBlob(blob, filename);
        } catch (err) {
          // User cancelled share or error occurred - fall back to download
          if (err instanceof Error && err.name !== 'AbortError') {
            downloadBlob(blob, filename);
          }
        }
      } else {
        downloadBlob(blob, filename);
      }
    });
  };

  const Icon = supportsShare ? IconShare : IconDownload;
  const label = supportsShare ? 'Share' : 'Download';
  const ariaLabel = supportsShare ? 'Share chart' : 'Download chart';

  return (
    <>
      <ActionIcon variant="subtle" color="gray" size="lg" onClick={handleClick} aria-label={ariaLabel} hiddenFrom="sm">
        <Icon size={18} />
      </ActionIcon>
      <Button
        variant="subtle"
        color="gray"
        size="compact-sm"
        onClick={handleClick}
        leftSection={<Icon size={16} />}
        visibleFrom="sm"
      >
        {label}
      </Button>
    </>
  );
}
